<template>
  <v-container>
    <loading-overlay :value="loading" />
    <div v-if="!loading">
      <div class="back-nav">
        <v-btn text @click="$router.back()">
          <v-icon left>mdi-chevron-left</v-icon>
          Back
        </v-btn>
      </div>
      <pt-sheet>
        <div class="page-x-padding">
          <v-row>
            <h1>
              Third Party Employee Monitoring Attestation
            </h1>
          </v-row>
          <v-row class="pb-4">
            <p>
              Please complete the following attestation. It is needed to monitor
              and comply with federal laws prohibiting Medicare and Medicaid
              fraud. Download an
              <a
                href="ExampleEmployeeMonitoringReport.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                example proof of compliance</a
              >.
            </p>
          </v-row>
          <v-row v-if="proofOfMonitoringText" class="pb-4">
            <p v-html="proofOfMonitoringText"></p>
          </v-row>
          <v-row justify="center">
            <v-col class="py-0" lg="9">
              <v-text-field
                outlined
                label="Number of Employees *"
                v-model="employees"
                :disabled="isComplete"
                :rules="[val => !!val || 'Required']"
              />
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="py-0" lg="9">
              <v-select
                outlined
                label="Employee Monitoring Service *"
                v-model="monitoringServiceUsed"
                :disabled="isComplete"
                :rules="[val => !!val || 'Required']"
                :items="[
                  'Certiphi Screening',
                  'Evercheck',
                  'HealthStream',
                  'KChecks',
                  'Medallion',
                  'Precheck',
                  'Streamline Verify',
                  'Verifiable',
                  'Verify Comply',
                  'Verisys',
                  'Other'
                ]"
              />
            </v-col>
          </v-row>
          <v-row justify="center" v-if="monitoringServiceUsed == 'Other'">
            <v-col class="py-0" lg="9">
              <v-text-field
                outlined
                label="Employee Monitoring Service Name *"
                v-model="monitoringServiceUsedOtherName"
                :disabled="isComplete"
                maxlength="130"
                :rules="[val => !!val || 'Required']"
              />
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="py-0" lg="9">
              <v-select
                outlined
                label="Did you identify any excluded employees this month? *"
                v-model="anyExcludedEmployeesIdentified"
                :disabled="isComplete"
                :items="[
                  { text: 'Yes', value: true },
                  { text: 'No', value: false }
                ]"
              />
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="py-0" lg="9">
              <v-textarea
                v-if="anyExcludedEmployeesIdentified"
                outlined
                rows="3"
                auto-grow
                label="If yes, how was it resolved? *"
                v-model="anyExcludedEmployeesIdentifiedResolution"
                :disabled="isComplete"
                :rules="[val => !!val || 'Required']"
              />
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col lg="9">
              <vue-dropzone
                id="flexBatchUpload"
                v-if="!isComplete"
                ref="myDropzone"
                :options="dropzoneOptions"
                class="flex-dropzone"
                :useCustomSlot="true"
                @vdropzone-file-added="fileUploading"
                @vdropzone-success="fileUploaded"
                @vdropzone-sending="addUserId"
              >
                <div>
                  <span>
                    <v-icon class="cloud-icon">mdi-cloud-upload</v-icon>
                  </span>
                  <div class="d-flex justify-center">
                    <h2>
                      Upload Proof of Compliance
                    </h2>
                  </div>
                  <div class="drag-or-browse">
                    Drag a PDF, Word, JPG or PNG file here or
                    <a href="#">browse</a>.
                  </div>
                </div>
              </vue-dropzone>
              <div v-if="isComplete">
                <div style="margin: 0 auto;text-align:center">
                  <v-icon class="cloud-icon">mdi-cloud-upload</v-icon>
                </div>
                <div class="d-flex justify-center">
                  <h2>
                    Proof of Compliance Uploaded
                  </h2>
                </div>
                <div class="filename">
                  <a :href="fileUrl">{{ filename }}</a>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-row justify="center" no-gutters>
            <v-col lg="8" xl="8">
              <v-checkbox
                :disabled="isComplete"
                label="By signing, I certify all information is true and correct to the best of my knowledge. "
                v-model="signatureChecked"
                :rules="[val => !!val || 'Required']"
              />
            </v-col>
          </v-row>
          <v-row justify="center" no-gutters>
            <v-col lg="8" xl="8">
              <v-checkbox
                :disabled="isComplete"
                label="I hereby attest to 3rd party monitoring. "
                v-model="attestationChecked"
                :rules="[val => !!val || 'Required']"
              />
            </v-col>
          </v-row>
          <v-row justify="center">
            <yellow-button
              v-if="!isComplete"
              :disabled="!canComplete"
              @click="submit"
              >Complete Attestation
            </yellow-button>
          </v-row>
        </div>
      </pt-sheet>
    </div>
  </v-container>
</template>
<script>
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import PtSheet from "@/components/PtSheet.vue";
import YellowButton from "@/components/providertrust/buttons/YellowButton";
import {
  customerChecklistByIdAPI,
  customerPOMChecklistByIdAPI,
  getPOMFileByChecklistIdAPI
} from "@/services/api";
import { get, put } from "@/services/request";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import numeral from "numeral";
import hasNotifications from "@/mixins/has-notifications";

export default {
  name: "ProofOfMonitoringSubmission",
  mixins: [hasNotifications],
  data: function() {
    return {
      dropzoneOptions: {
        url: "/services/files",
        maxFilesize: 100,
        headers: {
          "user-type": "vendor",
          "directory-name": "POM"
        },
        uploadMultiple: false,
        maxFiles: 1
      },
      employees: null,
      monitoringServiceUsed: null,
      monitoringServiceUsedOtherName: null,
      anyExcludedEmployeesIdentified: false,
      anyExcludedEmployeesIdentifiedResolution: null,
      signatureChecked: false,
      attestationChecked: false,
      proofOfMonitoringText: "",
      fileEntityId: null,
      vendorId: null,
      filename: null,
      isComplete: false,
      loading: false
    };
  },
  methods: {
    submit: function() {
      let proof = {
        fileEntityId: this.fileEntityId,
        employees: this.employees.replace(/[^0-9]/g, ""),
        monitoringServiceUsed: this.monitoringServiceUsed,
        monitoringServiceUsedOtherName: this.monitoringServiceUsedOtherName,
        anyExcludedEmployeesIdentified: this.anyExcludedEmployeesIdentified,
        anyExcludedEmployeesIdentifiedResolution: this
          .anyExcludedEmployeesIdentifiedResolution
      };
      put(
        customerPOMChecklistByIdAPI.getUrl({
          customerId: this.customerId,
          checklistItemId: this.checklistItemId
        }),
        proof
      )
        .then(response => {
          if (response) {
            this.$router.push({ name: "dashboard" });
          }
        })
        .catch(error => {
          this.showMessage({
            text: "Failed to save because " + error,
            mode: "error"
          });
        });
    },
    fileUploading: function() {
      let dropzone = this.$refs.myDropzone.dropzone;

      while (dropzone.files.length > dropzone.options.maxFiles) {
        dropzone.removeFile(dropzone.files[0]);
      }
    },
    fileUploaded: function(file, response) {
      if (response) {
        this.fileEntityId = response.id;
        this.filename = response.name;
      }
    },
    addUserId(file, xhr) {
      xhr.setRequestHeader("user-id", this.vendorId);
    },
    redirectIfNoProofOfMonitoringFile() {
      if (this.isComplete && this.fileEntityId == null) {
        // We assume that this POM was completed by VP employee monitoring instead of 3rd party, and thus
        // redirect them to view employees. Not ideal I know, but so be it
        this.$router.replace({
          name: "employees"
        });
      }
    }
  },
  components: {
    LoadingOverlay,
    "pt-sheet": PtSheet,
    "yellow-button": YellowButton,
    vueDropzone: vue2Dropzone
  },
  computed: {
    canComplete() {
      return (
        this.signatureChecked &&
        this.attestationChecked &&
        this.fileEntityId &&
        this.employees &&
        this.monitoringServiceUsed &&
        (this.monitoringServiceUsed !== "Other" ||
          this.monitoringServiceUsedOtherName) &&
        (this.anyExcludedEmployeesIdentified === false ||
          this.anyExcludedEmployeesIdentifiedResolution)
      );
    },
    fileUrl() {
      return getPOMFileByChecklistIdAPI.getUrl(this.checklistItemId);
    }
  },
  watch: {
    employees(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          const parsedValue = newValue.toString().replace(/[^0-9]/g, "");
          if (parsedValue === "") {
            this.employees = parsedValue;
            return;
          }
          this.employees = numeral(
            //postgres int4 max is 2.1 mil
            Math.min(1999999999, parsedValue)
          ).format("0,0");
        });
      }
    },
    monitoringServiceUsed(newValue) {
      if (newValue !== "Other") {
        this.monitoringServiceUsedOtherName = null;
      }
    },
    anyExcludedEmployeesIdentified(newValue) {
      if (newValue === false) {
        this.anyExcludedEmployeesIdentifiedResolution = null;
      }
    }
  },
  created() {
    this.loading = true;
    get(
      customerChecklistByIdAPI.getUrl({
        customerId: this.customerId,
        checklistItemId: this.checklistItemId
      })
    )
      .then(response => {
        if (response && response.data) {
          this.isComplete =
            response.data.status && response.data.status.name === "COMPLETE";
          this.signatureChecked = this.isComplete;
          this.attestationChecked = this.isComplete;

          ({
            proofOfMonitoringText: this.proofOfMonitoringText,
            fileEntityId: this.fileEntityId,
            filename: this.filename,
            vendorId: this.vendorId,
            employees: this.employees,
            monitoringServiceUsed: this.monitoringServiceUsed,
            monitoringServiceUsedOtherName: this.monitoringServiceUsedOtherName,
            anyExcludedEmployeesIdentified: this.anyExcludedEmployeesIdentified,
            anyExcludedEmployeesIdentifiedResolution: this
              .anyExcludedEmployeesIdentifiedResolution
          } = response.data);
        }
      })
      .finally(() => {
        this.loading = false;
        this.redirectIfNoProofOfMonitoringFile();
      });
  },
  props: ["checklistItemId", "customerId"]
};
</script>
<style lang="sass" scoped>

.page-x-padding
  @media (min-width: 768px)
    padding-left: 100px
    padding-right: 100px

.flex-dropzone
  border-radius: 32.5px
  background-color: rgba(10, 68, 159, 0.15)

.cloud-icon
  color: $primary
  opacity: .5
  font-size: 80px

.v-input--selection-controls
  margin-top: 0px

.checkbox-container
  margin: 48px 0

.drag-or-browse
  display: block
  color: black
  font-size: 20px
  line-height: 25px
  text-align: center
  margin: 8px auto
  max-width: 300px

.filename
  display: block
  color: black
  font-size: 20px
  line-height: 25px
  text-align: center
  margin: 8px auto
</style>
